import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, Table, Input, Button, Popconfirm, Progress, Tag, message, Tooltip, Image } from 'antd';
import {
  InboxOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import {
  uploadAnexos,
  listarAnexos,
  obterUrlStreamAnexo,
  obterUrlZipAnexos,
  deletarAnexo,
  obterObservacao,
  salvarObservacao,
  type AnexoGeral,
  type AnexoCategoria,
  type AnexoObservacao,
} from '../api/anexos';
import { useAuthStore } from '../store/authStore';

const { Dragger } = Upload;

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function iconePorMime(mime: string | null) {
  const m = mime || '';
  if (m.startsWith('image/')) return <FileImageOutlined className="text-lg" />;
  if (m === 'application/pdf') return <FilePdfOutlined className="text-lg" />;
  if (m.includes('zip') || m.includes('compressed')) return <FileZipOutlined className="text-lg" />;
  if (m.includes('spreadsheet') || m.includes('excel') || m === 'text/csv') return <FileExcelOutlined className="text-lg" />;
  if (m.includes('word') || m.includes('document')) return <FileWordOutlined className="text-lg" />;
  return <FileOutlined className="text-lg" />;
}

function tipoResumido(mime: string | null): string {
  const m = mime || '';
  if (m.startsWith('image/')) return 'Imagem';
  if (m === 'application/pdf') return 'PDF';
  if (m.includes('zip') || m.includes('compressed')) return 'ZIP';
  if (m.includes('spreadsheet') || m.includes('excel') || m === 'text/csv') return 'Planilha';
  if (m.includes('word') || m.includes('document')) return 'Documento';
  if (m.startsWith('video/')) return 'Vídeo';
  return 'Arquivo';
}

export default function AnexosPage() {
  const { clienteId: clienteIdParam } = useParams<{ clienteId: string }>();
  const clienteId = Number(clienteIdParam) || 0;
  const user = useAuthStore((s) => s.user);

  const [anexos, setAnexos] = useState<AnexoGeral[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');

  const [fila, setFila] = useState<UploadFile[]>([]);
  const [descricao, setDescricao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState(0);

  // Galerias de fotos por categoria (print do padrão/módulos, foto do coletivo)
  const [enviandoFoto, setEnviandoFoto] = useState<AnexoCategoria | null>(null);

  // Observação livre do cliente
  const [obsTexto, setObsTexto] = useState('');
  const [obsInfo, setObsInfo] = useState<AnexoObservacao | null>(null);
  const [obsSalvando, setObsSalvando] = useState(false);
  const obsCarregada = useRef('');

  const carregar = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      setAnexos(await listarAnexos(clienteId));
    } catch (err) {
      console.error(err);
      message.error('Erro ao carregar anexos.');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!clienteId) return;
    obterObservacao(clienteId)
      .then((obs) => {
        setObsInfo(obs);
        setObsTexto(obs?.texto || '');
        obsCarregada.current = obs?.texto || '';
      })
      .catch(console.error);
  }, [clienteId]);

  const prints = anexos.filter((a) => a.categoria === 'print_padrao_modulos');
  const fotosColetivo = anexos.filter((a) => a.categoria === 'foto_padrao_coletivo');
  const diversos = anexos.filter(
    (a) => a.categoria !== 'print_padrao_modulos' && a.categoria !== 'foto_padrao_coletivo'
  );

  const enviarFoto = async (file: File, categoria: AnexoCategoria) => {
    setEnviandoFoto(categoria);
    try {
      await uploadAnexos(clienteId, [file], {
        categoria,
        enviadoPor: user?.nome || user?.email || undefined,
      });
      message.success('Imagem enviada.');
      await carregar();
    } catch (err) {
      console.error(err);
      message.error('Erro ao enviar a imagem.');
    } finally {
      setEnviandoFoto(null);
    }
  };

  const salvarObs = async () => {
    if (obsTexto.trim() === obsCarregada.current.trim()) return;
    setObsSalvando(true);
    try {
      const obs = await salvarObservacao(clienteId, obsTexto, user?.nome || user?.email || undefined);
      setObsInfo(obs);
      obsCarregada.current = obs?.texto || '';
      message.success('Observação salva.');
    } catch (err) {
      console.error(err);
      message.error('Erro ao salvar observação.');
    } finally {
      setObsSalvando(false);
    }
  };

  const enviar = async () => {
    const files = fila.map((f) => f.originFileObj as File).filter(Boolean);
    if (!files.length) {
      message.warning('Selecione pelo menos um arquivo.');
      return;
    }
    setEnviando(true);
    setProgresso(0);
    try {
      await uploadAnexos(clienteId, files, {
        descricao: descricao.trim() || undefined,
        enviadoPor: user?.nome || user?.email || undefined,
        categoria: 'diversos',
        onProgress: setProgresso,
      });
      message.success(`${files.length} arquivo${files.length > 1 ? 's' : ''} enviado${files.length > 1 ? 's' : ''} com sucesso.`);
      setFila([]);
      setDescricao('');
      await carregar();
    } catch (err) {
      console.error(err);
      message.error('Erro ao enviar arquivos.');
    } finally {
      setEnviando(false);
      setProgresso(0);
    }
  };

  const abrir = (id: number) => {
    window.open(obterUrlStreamAnexo(id), '_blank', 'noopener');
  };

  const baixar = (id: number) => {
    window.open(obterUrlStreamAnexo(id, true), '_blank', 'noopener');
  };

  const excluir = async (id: number) => {
    try {
      await deletarAnexo(id);
      message.success('Anexo excluído.');
      setAnexos((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error(err);
      message.error('Erro ao excluir anexo.');
    }
  };

  const termo = busca.trim().toLowerCase();
  const filtrados = termo
    ? diversos.filter(
        (a) =>
          a.nome_original.toLowerCase().includes(termo) ||
          (a.descricao || '').toLowerCase().includes(termo) ||
          (a.enviado_por || '').toLowerCase().includes(termo)
      )
    : diversos;

  const columns = [
    {
      title: 'Arquivo',
      dataIndex: 'nome_original',
      key: 'nome',
      render: (nome: string, row: AnexoGeral) => (
        <button
          type="button"
          onClick={() => abrir(row.id)}
          className="flex items-center gap-2 text-left text-sm text-gray-900 hover:underline bg-transparent border-0 cursor-pointer p-0"
        >
          <span className="text-gray-400">{iconePorMime(row.mime_type)}</span>
          <span className="break-all">{nome}</span>
        </button>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'mime_type',
      key: 'tipo',
      width: 110,
      render: (m: string | null) => <Tag>{tipoResumido(m)}</Tag>,
    },
    {
      title: 'Tamanho',
      dataIndex: 'tamanho_bytes',
      key: 'tamanho',
      width: 100,
      render: (b: number | null) => <span className="text-gray-500 text-sm">{formatBytes(b)}</span>,
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      render: (d: string | null) => <span className="text-gray-500 text-sm">{d || '—'}</span>,
    },
    {
      title: 'Enviado por',
      dataIndex: 'enviado_por',
      key: 'enviado_por',
      width: 160,
      render: (e: string | null) => <span className="text-gray-500 text-sm">{e || '—'}</span>,
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'data',
      width: 150,
      render: (d: string) => <span className="text-gray-500 text-sm">{dayjs(d).format('DD/MM/YYYY HH:mm')}</span>,
    },
    {
      title: '',
      key: 'acoes',
      width: 110,
      render: (_: unknown, row: AnexoGeral) => (
        <div className="flex gap-1">
          <Tooltip title="Visualizar">
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => abrir(row.id)} />
          </Tooltip>
          <Tooltip title="Baixar">
            <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => baixar(row.id)} />
          </Tooltip>
          <Popconfirm
            title="Excluir este anexo?"
            description="Essa ação não pode ser desfeita."
            okText="Excluir"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => excluir(row.id)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Bloco de galeria de imagens de uma categoria (miniaturas + botão de upload).
  const galeriaFotos = (opts: {
    categoria: AnexoCategoria;
    titulo: string;
    descricao: string;
    itens: AnexoGeral[];
    rotuloBotao: string;
  }) => {
    const enviando = enviandoFoto === opts.categoria;
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-sm font-semibold text-gray-900">{opts.titulo}</h2>
          {opts.itens.length > 0 && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => window.open(obterUrlZipAnexos(clienteId, opts.categoria), '_blank', 'noopener')}
            >
              Baixar todas as imagens
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">{opts.descricao}</p>

        <div className="flex flex-wrap gap-3">
          {opts.itens.map((p) => (
            <div key={p.id} className="w-40">
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 h-28 flex items-center justify-center">
                {(p.mime_type || '').startsWith('image/') ? (
                  <Image src={obterUrlStreamAnexo(p.id)} alt={p.nome_original} height={112} style={{ objectFit: 'cover' }} />
                ) : (
                  <button
                    type="button"
                    onClick={() => abrir(p.id)}
                    className="text-gray-400 bg-transparent border-0 cursor-pointer"
                  >
                    {iconePorMime(p.mime_type)}
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500 truncate" title={p.nome_original}>
                  {p.nome_original}
                </span>
                <Popconfirm
                  title="Excluir esta imagem?"
                  okText="Excluir"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => excluir(p.id)}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          ))}

          <Upload
            accept="image/*,.pdf"
            showUploadList={false}
            beforeUpload={(file) => {
              enviarFoto(file as File, opts.categoria);
              return false;
            }}
            disabled={enviando}
          >
            <button
              type="button"
              disabled={enviando}
              className="w-40 h-28 border-2 border-dashed border-gray-300 rounded-lg bg-transparent cursor-pointer text-gray-400 hover:border-gray-900 hover:text-gray-900 transition-colors flex flex-col items-center justify-center gap-1"
            >
              <InboxOutlined className="text-xl" />
              <span className="text-xs">{enviando ? 'Enviando...' : opts.rotuloBotao}</span>
            </button>
          </Upload>
        </div>
      </div>
    );
  };

  if (!clienteId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-900 font-medium">Cliente não informado</p>
          <p className="text-sm text-gray-500 mt-1">Acesse esta página pelo card do cliente no Pipefy.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Anexos do Cliente</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cliente {clienteId} — upload de arquivos avulsos: fotos, PDFs, documentos etc.
          </p>
        </div>

        {galeriaFotos({
          categoria: 'print_padrao_modulos',
          titulo: 'Print do local do padrão e dos módulos',
          descricao:
            'Envie a imagem (print de mapa/foto) já com a marcação de onde fica o padrão de energia e onde ficarão os módulos.',
          itens: prints,
          rotuloBotao: 'Adicionar print',
        })}

        {galeriaFotos({
          categoria: 'foto_padrao_coletivo',
          titulo: 'Foto coletivo com marcação do padrão da usina',
          descricao:
            'Em caso de padrão coletivo, anexar foto ampla e demarcar em qual caixa será instalada a usina solar.',
          itens: fotosColetivo,
          rotuloBotao: 'Adicionar foto',
        })}

        {/* Área de upload — anexos diversos */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Anexos diversos</h2>
          <Dragger
            multiple
            fileList={fila}
            beforeUpload={() => false}
            onChange={({ fileList }) => setFila(fileList)}
            disabled={enviando}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: '#111' }} />
            </p>
            <p className="ant-upload-text">Clique ou arraste arquivos aqui</p>
            <p className="ant-upload-hint">Suporta múltiplos arquivos. Máx. 50 MB por arquivo.</p>
          </Dragger>

          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Descrição (opcional)"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={500}
              disabled={enviando}
            />
            <Button
              type="primary"
              onClick={enviar}
              loading={enviando}
              disabled={!fila.length}
              style={{ background: '#111', minWidth: 140 }}
            >
              Enviar {fila.length > 0 ? `(${fila.length})` : ''}
            </Button>
          </div>

          {enviando && <Progress percent={progresso} size="small" strokeColor="#111" className="mt-3" />}
        </div>

        {/* Observações */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Observações</h2>
          <p className="text-xs text-gray-500 mb-3">
            Anotações livres sobre o cliente / instalação — visível para quem abrir esta página.
          </p>
          <Input.TextArea
            value={obsTexto}
            onChange={(e) => setObsTexto(e.target.value)}
            rows={4}
            maxLength={5000}
            placeholder="Escreva aqui qualquer observação relevante..."
            disabled={obsSalvando}
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-400">
              {obsInfo
                ? `Última atualização: ${obsInfo.atualizado_por || '—'} em ${dayjs(obsInfo.updated_at).format('DD/MM/YYYY HH:mm')}`
                : 'Nenhuma observação salva ainda.'}
            </span>
            <Button
              type="primary"
              onClick={salvarObs}
              loading={obsSalvando}
              disabled={obsTexto.trim() === obsCarregada.current.trim()}
              style={{ background: '#111' }}
            >
              Salvar observação
            </Button>
          </div>
        </div>

        {/* Listagem */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex flex-col sm:flex-row gap-3 justify-between mb-4">
            <Input.Search
              placeholder="Buscar por nome, descrição ou quem enviou..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              allowClear
              style={{ maxWidth: 360 }}
            />
            <div className="flex gap-2">
              {anexos.length > 0 && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(obterUrlZipAnexos(clienteId), '_blank', 'noopener')}
                >
                  Baixar todos os anexos
                </Button>
              )}
              <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>
                Atualizar
              </Button>
            </div>
          </div>

          <Table
            rowKey="id"
            dataSource={filtrados}
            columns={columns}
            loading={loading}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `${t} anexo${t !== 1 ? 's' : ''}` }}
            scroll={{ x: 800 }}
          />
        </div>
      </div>
    </div>
  );
}
